/* Church Sign Shop — sign template definitions + canvas rendering engine.
   One source of truth: the editor preview, the template gallery tiles, and
   the final print file all draw through SignTemplates.render(), so what the
   customer sees is exactly what production prints.

   Template text is LOCKED (message + accent lines are not editable).
   Customers change: background color, text color, an optional logo, and an
   optional #hashtag. The logo + hashtag render as a bottom row (logo box ·
   divider · hashtag), matching the shop's production layout
   (Standard_Greeting_22x22_ForExport.pdf). Exposes window.SignTemplates.    */
(function () {
  "use strict";

  /* ── Palette (the theme's CMYK-forward system) ─────────────────────── */
  var PALETTE = {
    magenta:    { css: "rgb(234,0,138)", name: "Magenta" },
    yellow:     { css: "rgb(255,240,0)", name: "Yellow" },
    cyan:       { css: "rgb(0,172,237)", name: "Cyan" },
    ink:        { css: "rgb(35,31,32)",  name: "Ink" },
    white:      { css: "#FFFFFF",        name: "White" },
    maroon:     { css: "#5E1C2C",        name: "Maroon" },
    navy:       { css: "#1B2A4A",        name: "Navy" },
    forest:     { css: "#1E4D3B",        name: "Forest" },
    pinkTint:   { css: "#FDE5F4",        name: "Pink tint" },
    yellowTint: { css: "#FFF880",        name: "Yellow tint" },
    cyanTint:   { css: "#E5F7FD",        name: "Cyan tint" }
  };
  var BG_OPTIONS = ["magenta", "yellow", "cyan", "ink", "white", "maroon",
                    "navy", "forest", "pinkTint", "yellowTint", "cyanTint"];
  var TEXT_OPTIONS = ["auto", "ink", "white", "magenta", "cyan", "yellow"];

  var SHAPES = {
    square:    { wIn: 22, hIn: 22, circle: false, label: 'Square · 22" × 22"' },
    rectangle: { wIn: 18, hIn: 24, circle: false, label: 'Rectangle · 18" × 24"' },
    circle:    { wIn: 22, hIn: 22, circle: true,  label: 'Circle · 22" Diameter' }
  };

  function isHex(v) { return typeof v === "string" && v.charAt(0) === "#"; }

  /* Relative luminance → pick ink or white for legibility on a background. */
  function contrastColor(cssColor) {
    var m = cssColor.replace("#", "");
    var r, g, b;
    if (cssColor.indexOf("rgb") === 0) {
      var p = cssColor.match(/\d+/g);
      r = +p[0]; g = +p[1]; b = +p[2];
    } else {
      if (m.length === 3) m = m[0] + m[0] + m[1] + m[1] + m[2] + m[2];
      r = parseInt(m.substr(0, 2), 16);
      g = parseInt(m.substr(2, 2), 16);
      b = parseInt(m.substr(4, 2), 16);
    }
    var lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return lum > 0.55 ? PALETTE.ink.css : PALETTE.white.css;
  }

  /* ── Footer row geometry (physical inches — consistent on every shape).
     Logo · divider · #hashtag are laid out as ONE GROUP with equal gaps
     around the divider, and the whole group is centered horizontally (so
     the divider is only at template center when logo and hashtag happen to
     be the same width). The group sits a fixed distance above the trim
     bottom.                                                                */
  var FOOTER = {
    bottomIn: 1.8,        /* gap between group bottom and the sign's bottom edge */
    rowHIn: 2.0,          /* group height = logo box height */
    gapIn: 0.65,          /* logo↔divider and divider↔hashtag spacing */
    dividerWIn: 0.09,
    dividerHIn: 2.2,
    logoMaxWIn: 5.4,
    hashtagSizeIn: 1.05,  /* Bebas font size */
    hashtagSpacing: 0.06, /* letter-spacing, em */
    hashtagMaxWIn: 10.5
  };

  /* The editable hashtag field, shared by every template. */
  function footerEl() {
    return { type: "footer", id: "hashtag", label: "#Hashtag (optional)", text: "" };
  }

  /* ── Template definitions ──────────────────────────────────────────────
     Coordinates are fractions of the TRIM box (0..1). `y` is the vertical
     center of the element. `size` is font size as a fraction of trim height.
     Message/accent text is fixed (no id → not editable). `color:"auto"`
     follows the customer's text color choice.                              */
  var TEMPLATES = {
    1: { name: "You Belong Here", tag: "Welcome", bg: "magenta",
      elements: [
        { type: "text", text: "YOU\nBELONG\nHERE",
          font: "display", size: 0.155, y: 0.4, maxW: 0.72, color: "auto", spacing: 0.02 },
        { type: "bar", y: 0.66, w: 0.16, h: 0.012, color: "auto" },
        footerEl()
      ] },
    2: { name: "Welcome Home", tag: "Welcome", bg: "yellow",
      elements: [
        { type: "text", text: "WELCOME",
          font: "display", size: 0.16, y: 0.34, maxW: 0.74, color: "auto", spacing: 0.02 },
        { type: "text", text: "home",
          font: "body", italic: true, weight: 500, size: 0.14, y: 0.5, maxW: 0.6, color: "magenta" },
        footerEl()
      ] },
    3: { name: "So Glad You're Here", tag: "Welcome", bg: "cyan",
      elements: [
        { type: "text", text: "SO GLAD\nYOU'RE\nHERE",
          font: "display", size: 0.145, y: 0.4, maxW: 0.72, color: "auto", spacing: 0.02 },
        { type: "bar", y: 0.65, w: 0.16, h: 0.012, color: "yellow" },
        footerEl()
      ] },
    4: { name: "Smile, It's Sunday", tag: "Holidays", bg: "pinkTint",
      elements: [
        { type: "text", text: "SMILE,",
          font: "display", size: 0.19, y: 0.34, maxW: 0.74, color: "magenta", spacing: 0.02 },
        { type: "text", text: "IT'S SUNDAY",
          font: "display", size: 0.085, y: 0.5, maxW: 0.7, color: "auto", spacing: 0.08 },
        footerEl()
      ] },
    5: { name: "We Are So Glad You Are Here", tag: "Welcome", bg: "ink",
      elements: [
        { type: "bar", y: 0.16, w: 0.16, h: 0.012, color: "yellow" },
        { type: "text", text: "WE ARE\nSO GLAD\nYOU ARE\nHERE",
          font: "display", size: 0.115, y: 0.42, maxW: 0.72, color: "auto", spacing: 0.03 },
        footerEl()
      ] },
    6: { name: "First Time? You're a Big Deal", tag: "Special Events", bg: "yellowTint",
      elements: [
        { type: "text", text: "FIRST\nTIME?",
          font: "display", size: 0.17, y: 0.36, maxW: 0.72, color: "auto", spacing: 0.02 },
        { type: "text", text: "you're a big deal",
          font: "body", italic: true, weight: 500, size: 0.075, y: 0.56, maxW: 0.74, color: "magenta" },
        footerEl()
      ] },
    7: { name: "You Look Amazing Today", tag: "Welcome", bg: "maroon",
      elements: [
        { type: "text", text: "YOU LOOK",
          font: "display", size: 0.115, y: 0.28, maxW: 0.76, color: "auto", spacing: 0.02 },
        { type: "text", text: "Amazing",
          font: "body", italic: true, weight: 600, size: 0.17, y: 0.42, maxW: 0.8, color: "auto" },
        { type: "text", text: "TODAY!",
          font: "display", size: 0.115, y: 0.56, maxW: 0.76, color: "auto", spacing: 0.02 },
        footerEl()
      ] },
    8: { name: "It's Great to See You", tag: "Welcome", bg: "cyanTint",
      elements: [
        { type: "text", text: "IT'S GREAT\nTO SEE\nYOU",
          font: "display", size: 0.14, y: 0.4, maxW: 0.74, color: "cyan", spacing: 0.02 },
        { type: "bar", y: 0.64, w: 0.16, h: 0.012, color: "auto" },
        footerEl()
      ] },
    9: { name: "Honk If You're Expecting Great Things", tag: "Seasonal", bg: "yellow",
      elements: [
        { type: "text", text: "HONK",
          font: "display", size: 0.21, y: 0.32, maxW: 0.76, color: "auto", spacing: 0.02 },
        { type: "text", text: "IF YOU'RE EXPECTING\nGREAT THINGS",
          font: "display", size: 0.062, y: 0.52, maxW: 0.72, color: "magenta", spacing: 0.06 },
        footerEl()
      ] },
    10: { name: "You Can Sit With Us", tag: "Welcome", bg: "navy",
      elements: [
        { type: "text", text: "YOU CAN\nSIT WITH\nUS",
          font: "display", size: 0.14, y: 0.4, maxW: 0.74, color: "auto", spacing: 0.02 },
        { type: "bar", y: 0.64, w: 0.16, h: 0.012, color: "magenta" },
        footerEl()
      ] },
    11: { name: "The Best Is Yet To Come", tag: "Holidays", bg: "cyan",
      elements: [
        { type: "text", text: "THE BEST\nIS YET\nTO COME",
          font: "display", size: 0.14, y: 0.4, maxW: 0.74, color: "auto", spacing: 0.02 },
        { type: "bar", y: 0.64, w: 0.16, h: 0.012, color: "yellow" },
        footerEl()
      ] },
    12: { name: "Welcome, Friend", tag: "Welcome", bg: "white",
      elements: [
        { type: "text", text: "WELCOME,",
          font: "display", size: 0.13, y: 0.34, maxW: 0.76, color: "auto", spacing: 0.02 },
        { type: "text", text: "FRIEND",
          font: "display", size: 0.155, y: 0.48, maxW: 0.76, color: "magenta", spacing: 0.02 },
        footerEl()
      ] }
  };

  /* ── Helpers ───────────────────────────────────────────────────────── */
  function resolveBg(design, tpl, fallback) {
    if (design && isHex(design.bg)) return design.bg;
    if (design && design.bg && PALETTE[design.bg]) return PALETTE[design.bg].css;
    if (tpl && tpl.bg && PALETTE[tpl.bg]) return PALETTE[tpl.bg].css;
    if (isHex(fallback)) return fallback;
    if (fallback && PALETTE[fallback]) return PALETTE[fallback].css;
    return "#FFFFFF";
  }

  function resolveColor(spec, design, bgCss) {
    if (spec === "auto") {
      if (design.textColor && design.textColor !== "auto") {
        if (isHex(design.textColor)) return design.textColor;
        if (PALETTE[design.textColor]) return PALETTE[design.textColor].css;
      }
      return contrastColor(bgCss);
    }
    if (PALETTE[spec]) return PALETTE[spec].css;
    return spec; /* raw css */
  }

  function fontString(el, px) {
    var family = el.font === "display" ? '"Bebas Neue"' : '"Roboto"';
    var weight = el.weight || (el.font === "display" ? 400 : 700);
    var style = el.italic ? "italic " : "";
    return style + weight + " " + px + 'px ' + family;
  }

  function widestLine(ctx, lines, el, px) {
    ctx.font = fontString(el, px);
    if ("letterSpacing" in ctx) ctx.letterSpacing = ((el.spacing || 0) * px) + "px";
    var w = 0;
    for (var i = 0; i < lines.length; i++) {
      w = Math.max(w, ctx.measureText(lines[i]).width);
    }
    return w;
  }

  function drawTextBlock(ctx, el, value, cx, trimY, trimW, trimH) {
    var lines = value.replace(/\r/g, "").split("\n").map(function (l) { return l.trim(); })
                     .filter(function (l) { return l.length; });
    if (!lines.length) return;
    var px = el.size * trimH;
    var maxWpx = el.maxW * trimW;
    var wide = widestLine(ctx, lines, el, px);
    if (wide > maxWpx) px = px * (maxWpx / wide);
    ctx.font = fontString(el, px);
    if ("letterSpacing" in ctx) ctx.letterSpacing = ((el.spacing || 0) * px) + "px";
    var lh = px * (el.font === "display" ? 1.04 : 1.25);
    var total = lh * lines.length;
    var startY = trimY + el.y * trimH - total / 2 + lh / 2;
    lines.forEach(function (line, i) {
      ctx.fillText(line, cx, startY + i * lh);
    });
    if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
  }

  /* Bottom row: [logo · divider · #hashtag] laid out as one group, equal
     gaps around the divider, the whole group centered horizontally and
     anchored FOOTER.bottomIn above the trim bottom. Preview = print:
     only-logo or only-hashtag centers alone with no divider.               */
  function drawFooter(ctx, el, design, opts, g) {
    var hashtag = (design.fields && typeof design.fields[el.id] === "string")
      ? design.fields[el.id].trim() : (el.text || "");
    var logo = opts.logoImg && opts.logoImg.width ? opts.logoImg : null;
    /* preview = print: logo only → centered logo; hashtag only → centered
       hashtag; the divider exists only when BOTH are present */
    if (!logo && !hashtag) return;

    var ppi = g.ppi;
    var color = resolveColor("auto", design, g.bgCss);
    var rowH = FOOTER.rowHIn * ppi;
    var yC = g.trimY + g.trimH - FOOTER.bottomIn * ppi - rowH / 2;
    var gap = FOOTER.gapIn * ppi;

    /* measure each piece */
    var logoW = 0, logoH = 0;
    if (logo) {
      var scale = (opts.logoScale > 0) ? opts.logoScale : 1;
      var s = Math.min((FOOTER.logoMaxWIn * ppi) / logo.width, rowH / logo.height) * scale;
      logoW = logo.width * s; logoH = logo.height * s;
    }

    var hashW = 0, hashPx = 0;
    if (hashtag) {
      hashPx = FOOTER.hashtagSizeIn * ppi;
      var hEl = { font: "display", spacing: FOOTER.hashtagSpacing };
      hashW = widestLine(ctx, [hashtag], hEl, hashPx);
      var maxW = FOOTER.hashtagMaxWIn * ppi;
      if (hashW > maxW) {
        hashPx = hashPx * (maxW / hashW);
        hashW = maxW;
      }
    }

    var hasLogoSide = logoW > 0;
    var showDivider = hasLogoSide && hashtag;
    var divW = showDivider ? FOOTER.dividerWIn * ppi : 0;
    var total = logoW + (showDivider ? gap + divW + gap : 0) + hashW;
    var x = g.trimX + g.trimW / 2 - total / 2;

    /* logo */
    if (logo) {
      ctx.drawImage(logo, x, yC - logoH / 2, logoW, logoH);
    }
    x += logoW;

    /* divider */
    if (showDivider) {
      x += gap;
      ctx.fillStyle = color;
      ctx.fillRect(x, yC - (FOOTER.dividerHIn * ppi) / 2, divW, FOOTER.dividerHIn * ppi);
      x += divW + gap;
    }

    /* hashtag — centered on the measured glyph box, not the em box (Bebas
       carries descender space that would push the caps above the visual
       centerline of the logo + divider) */
    if (hashtag) {
      var hEl2 = { font: "display", spacing: FOOTER.hashtagSpacing };
      ctx.save();
      ctx.font = fontString(hEl2, hashPx);
      if ("letterSpacing" in ctx) ctx.letterSpacing = (FOOTER.hashtagSpacing * hashPx) + "px";
      ctx.fillStyle = color;
      /* metrics are relative to the ACTIVE baseline — switch first, then measure */
      ctx.textBaseline = "alphabetic";
      var tm = ctx.measureText(hashtag);
      if (typeof tm.actualBoundingBoxAscent === "number") {
        var baseY = yC + (tm.actualBoundingBoxAscent - tm.actualBoundingBoxDescent) / 2;
        ctx.fillText(hashtag, x + hashW / 2, baseY);
      } else {
        ctx.textBaseline = "middle";
        ctx.fillText(hashtag, x + hashW / 2, yC);
      }
      ctx.restore();
    }
  }

  /* ── Main render ────────────────────────────────────────────────────
     opts: { ctx, templateId, design, shape ('square'|'rectangle'|'circle'),
             bleedIn, ppi, logoImg (Image|null), guides (bool) }
     Canvas must already be sized (wIn+2*bleed)*ppi × (hIn+2*bleed)*ppi.   */
  function render(opts) {
    var ctx = opts.ctx;
    var tpl = TEMPLATES[opts.templateId];
    if (!tpl) return;
    var design = opts.design || {};
    var shape = SHAPES[opts.shape] || SHAPES.square;
    var b = (typeof opts.bleedIn === "number" ? opts.bleedIn : 1);
    var ppi = opts.ppi;
    var g = {
      W: (shape.wIn + 2 * b) * ppi,
      H: (shape.hIn + 2 * b) * ppi,
      trimX: b * ppi, trimY: b * ppi,
      trimW: shape.wIn * ppi, trimH: shape.hIn * ppi,
      ppi: ppi,
      bgCss: resolveBg(design, tpl)
    };

    ctx.save();
    ctx.fillStyle = g.bgCss;
    ctx.fillRect(0, 0, g.W, g.H);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    var cx = g.trimX + g.trimW / 2;

    tpl.elements.forEach(function (el) {
      if (el.type === "bar") {
        ctx.fillStyle = resolveColor(el.color, design, g.bgCss);
        var bw = el.w * g.trimW, bh = Math.max(2, el.h * g.trimH);
        ctx.fillRect(cx - bw / 2, g.trimY + el.y * g.trimH - bh / 2, bw, bh);
        return;
      }
      if (el.type === "footer") {
        drawFooter(ctx, el, design, opts, g);
        return;
      }
      if (el.type !== "text") return;
      ctx.fillStyle = resolveColor(el.color, design, g.bgCss);
      drawTextBlock(ctx, el, el.text, cx, g.trimY, g.trimW, g.trimH);
    });

    /* bleed furniture only on request (upload preflight); the finished-sign
       preview crops to trim instead */
    if (opts.bleedGuides) drawGuides(ctx, shape, b, ppi);
    ctx.restore();
  }

  /* Editor-preview guides: dim the bleed zone, dash the trim/cut line. */
  function drawGuides(ctx, shape, b, ppi) {
    var W = (shape.wIn + 2 * b) * ppi;
    var H = (shape.hIn + 2 * b) * ppi;
    var trimX = b * ppi, trimY = b * ppi;
    var trimW = shape.wIn * ppi, trimH = shape.hIn * ppi;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, W, H);
    if (shape.circle) {
      ctx.arc(W / 2, H / 2, trimW / 2, 0, Math.PI * 2, true);
    } else {
      ctx.rect(trimX + trimW, trimY, -trimW, trimH); /* reverse winding */
    }
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fill("evenodd");

    ctx.beginPath();
    if (shape.circle) {
      ctx.arc(W / 2, H / 2, trimW / 2, 0, Math.PI * 2);
    } else {
      ctx.rect(trimX, trimY, trimW, trimH);
    }
    ctx.setLineDash([Math.max(4, ppi * 0.12), Math.max(4, ppi * 0.08)]);
    ctx.lineWidth = Math.max(1, ppi * 0.02);
    ctx.strokeStyle = "rgba(35,31,32,0.8)";
    ctx.stroke();
    ctx.restore();
  }

  /* ── Overlay (design-layer PNG) rendering ──────────────────────────────
     The shop supplies each design as a full-sheet transparent PNG (e.g.
     7200×7200 = 24"×24" at 300 DPI): WHITE artwork only — no anchor marks
     (the PDF writer draws those as vectors). Layers compose as:
     BG color → design PNG (white or tinted black) → logo/#hashtag footer.  */

  var tintCache = {};
  var tintKeys = [];
  var TINT_KEEP = 3;

  /* Tint the white artwork to a color (design PNGs are art only — the CNC
     anchor marks are drawn as vectors by the PDF writer). */
  function tintedOverlay(img, color) {
    var key = (img.src || "") + "|" + color;
    if (tintCache[key]) return tintCache[key];
    var cv = document.createElement("canvas");
    cv.width = img.width; cv.height = img.height;
    var ctx = cv.getContext("2d");
    ctx.drawImage(img, 0, 0);
    ctx.globalCompositeOperation = "source-in";
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.globalCompositeOperation = "source-over";
    tintCache[key] = cv;
    tintKeys.push(key);
    while (tintKeys.length > TINT_KEEP) {
      var oldKey = tintKeys.shift();
      var old = tintCache[oldKey];
      if (old) { old.width = old.height = 1; } /* release the bitmap */
      delete tintCache[oldKey];
    }
    return cv;
  }

  /* Render a design-overlay template onto a full-bleed canvas.
     opts: { ctx, img, design {bg (hex/palette key), ink ('white'|'black'),
             fields {hashtag}}, shape, bleedIn, ppi, logoImg,
             markRects, sheetMatches (overlay sheet == this shape's sheet),
             defaultBg }                                                     */
  function renderOverlay(opts) {
    var ctx = opts.ctx;
    var design = opts.design || {};
    var shape = SHAPES[opts.shape] || SHAPES.square;
    var b = (typeof opts.bleedIn === "number" ? opts.bleedIn : 1);
    var ppi = opts.ppi;
    var g = {
      W: (shape.wIn + 2 * b) * ppi,
      H: (shape.hIn + 2 * b) * ppi,
      trimX: b * ppi, trimY: b * ppi,
      trimW: shape.wIn * ppi, trimH: shape.hIn * ppi,
      ppi: ppi,
      bgCss: resolveBg(design, { bg: null }, opts.defaultBg)
    };

    ctx.save();
    ctx.fillStyle = g.bgCss;
    ctx.fillRect(0, 0, g.W, g.H);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    var img = opts.img;
    if (img && img.width) {
      var ink = design.ink === "black" ? PALETTE.ink.css : null;
      var src = ink ? tintedOverlay(img, ink) : img;
      if (opts.sheetMatches) {
        /* overlay sheet == target sheet: draw 1:1, dots land exactly */
        ctx.drawImage(src, 0, 0, g.W, g.H);
      } else {
        /* different sheet (e.g. square art on the rectangle sign): crop the
           overlay to its trim region (marks live in the bleed and fall away)
           and contain-fit the art into this sign's trim box */
        var sPx = img.width; /* square overlay = square sheet (22" + 2×bleed) */
        var srcB = sPx * (b / (SHAPES.square.wIn + 2 * b));
        var srcTrim = sPx - 2 * srcB;
        var fit = Math.min(g.trimW / srcTrim, g.trimH / srcTrim);
        var dw = srcTrim * fit, dh = srcTrim * fit;
        ctx.drawImage(src, srcB, srcB, srcTrim, srcTrim,
                      g.trimX + (g.trimW - dw) / 2, g.trimY + (g.trimH - dh) / 2, dw, dh);
      }
    }

    /* footer color follows the ink toggle */
    var footerDesign = {
      bg: design.bg, fields: design.fields,
      textColor: design.ink === "black" ? "ink" : "white"
    };
    drawFooter(ctx, { id: "hashtag", text: "" }, footerDesign,
               { logoImg: opts.logoImg, logoScale: opts.logoScale, guides: opts.guides }, g);
    ctx.restore();
  }

  /* Finished-sign preview: render the full sheet offscreen via drawFull(ctx,
     ppi), then blit only the trim area into `canvas` with rounded corners
     (or a circle). Bleed, anchor dots and crop furniture never show.        */
  function renderPreviewInto(canvas, cssW, opts) {
    var shape = SHAPES[opts.shape] || SHAPES.square;
    var b = (typeof opts.bleedIn === "number" ? opts.bleedIn : 1);
    var dpr = opts.dpr || 1;
    var ppi = (cssW * dpr) / shape.wIn;
    var off = document.createElement("canvas");
    off.width = Math.round((shape.wIn + 2 * b) * ppi);
    off.height = Math.round((shape.hIn + 2 * b) * ppi);
    opts.drawFull(off.getContext("2d"), ppi);

    canvas.width = Math.round(shape.wIn * ppi);
    canvas.height = Math.round(shape.hIn * ppi);
    canvas.style.width = cssW + "px";
    /* round the ELEMENT too (same radius as the drawn mask) so CSS
       box-shadows follow the sign shape instead of the square canvas */
    canvas.style.borderRadius = shape.circle
      ? "50%"
      : ((opts.cornerRadiusIn || 0) * (cssW / shape.wIn)) + "px";
    var ctx = canvas.getContext("2d");
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(off, -Math.round(b * ppi), -Math.round(b * ppi));
    /* round the corners with an antialiased mask — clip() gives jagged
       edges in Safari/WebKit */
    ctx.globalCompositeOperation = "destination-in";
    ctx.beginPath();
    if (shape.circle) {
      ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2);
    } else {
      var r = Math.max(0, (opts.cornerRadiusIn || 0) * ppi);
      var w = canvas.width, h = canvas.height;
      r = Math.min(r, w / 2, h / 2);
      ctx.moveTo(r, 0);
      ctx.arcTo(w, 0, w, h, r);
      ctx.arcTo(w, h, 0, h, r);
      ctx.arcTo(0, h, 0, 0, r);
      ctx.arcTo(0, 0, w, 0, r);
      ctx.closePath();
    }
    ctx.fillStyle = "#000";
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    ctx.restore();
  }

  /* Cover-fit an uploaded image onto the full-bleed canvas (upload path). */
  function renderUpload(opts) {
    var ctx = opts.ctx, img = opts.img;
    var shape = SHAPES[opts.shape] || SHAPES.square;
    var b = (typeof opts.bleedIn === "number" ? opts.bleedIn : 1);
    var ppi = opts.ppi;
    var W = (shape.wIn + 2 * b) * ppi;
    var H = (shape.hIn + 2 * b) * ppi;
    ctx.save();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, W, H);
    if (img && img.width) {
      var s = Math.max(W / img.width, H / img.height);
      var dw = img.width * s, dh = img.height * s;
      ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
    }
    if (opts.guides) drawGuides(ctx, shape, b, ppi);
    ctx.restore();
  }

  /* Effective print resolution of an upload, cover-fit to shape + bleed. */
  function uploadPpi(imgW, imgH, shapeKey, bleedIn) {
    var shape = SHAPES[shapeKey] || SHAPES.square;
    var b = (typeof bleedIn === "number" ? bleedIn : 1);
    var tW = shape.wIn + 2 * b, tH = shape.hIn + 2 * b;
    var imgAspect = imgW / imgH, tAspect = tW / tH;
    return imgAspect > tAspect ? imgH / tH : imgW / tW;
  }

  /* Preload the theme fonts so canvas text renders correctly. */
  function loadFonts() {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return Promise.all([
      document.fonts.load('400 100px "Bebas Neue"'),
      document.fonts.load('500 100px "Roboto"'),
      document.fonts.load('700 100px "Roboto"'),
      document.fonts.load('italic 500 100px "Roboto"'),
      document.fonts.load('italic 600 100px "Roboto"')
    ]).catch(function () { /* render with fallbacks rather than fail */ });
  }

  window.SignTemplates = {
    PALETTE: PALETTE,
    BG_OPTIONS: BG_OPTIONS,
    TEXT_OPTIONS: TEXT_OPTIONS,
    SHAPES: SHAPES,
    TEMPLATES: TEMPLATES,
    FOOTER: FOOTER,
    contrastColor: contrastColor,
    render: render,
    renderOverlay: renderOverlay,
    renderPreviewInto: renderPreviewInto,
    renderUpload: renderUpload,
    uploadPpi: uploadPpi,
    loadFonts: loadFonts
  };
})();
